import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { cn } from "../lib/utils";

const LETTERS = ["G", "u", "r", "S", "e", "t", "u"];

/*
  2 sec cinematic intro
  + 10 sec slow shine
  = 12 sec full loop
*/
const CYCLE_MS = 12000;

const SIZE_MAP = {
  sm: {
    wordSize: 28,
    iconSize: 20,
    lineWidth: 54,
    gap: 1,
    maxWidth: 240,
  },
  md: {
    wordSize: 44,
    iconSize: 24,
    lineWidth: 72,
    gap: 1.5,
    maxWidth: 360,
  },
  lg: {
    wordSize: 56,
    iconSize: 30,
    lineWidth: 92,
    gap: 2,
    maxWidth: 460,
  },
  xl: {
    wordSize: 68,
    iconSize: 36,
    lineWidth: 110,
    gap: 2.4,
    maxWidth: 560,
  },
};

export function GurSetuWordmark({
  size = "lg",
  onPress,
  tagline,
  testID,
  className,
  loop = true,
}) {
  const [cycle, setCycle] = useState(0);
  const config = SIZE_MAP[size] || SIZE_MAP.lg;

  useEffect(() => {
    if (!loop) {
      return undefined;
    }

    const id = setInterval(() => {
      setCycle((prev) => prev + 1);
    }, CYCLE_MS);

    return () => clearInterval(id);
  }, [loop]);

  const Content = (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center select-none overflow-visible pt-2 pb-1",
        className
      )}
      data-testid={testID}
    >
      {/* ===============================
          SOFT BACKGROUND GLOW
      =============================== */}
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          key={`glow-a-${cycle}`}
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: [0, 0.22, 0.12], scale: [0.7, 1.08, 1] }}
          transition={{ duration: 2.4, ease: "easeOut" }}
          className="absolute left-1/2 top-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-300/20 blur-3xl"
        />
        <motion.div
          key={`glow-b-${cycle}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.14, 0.06] }}
          transition={{ duration: 2.8, ease: "easeOut", delay: 0.25 }}
          className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full bg-yellow-200/10 blur-[70px]"
        />
      </div>

      {/* ===============================
          CENTER IK ONKAR INTRO
      =============================== */}
      <motion.div
        key={`center-ik-${cycle}`}
        initial={{
          opacity: 0,
          scale: 0.55,
          y: 20,
          rotateX: -80,
        }}
        animate={{
          opacity: [0, 1, 1, 0],
          scale: [0.55, 1.18, 1.08, 0.85],
          y: [20, 0, 0, -16],
          rotateX: [-80, 0, 0, 0],
        }}
        transition={{
          duration: 1.8,
          ease: "easeOut",
          times: [0, 0.28, 0.72, 1],
        }}
        className="pointer-events-none absolute left-1/2 top-1/2 z-30 -translate-x-1/2 -translate-y-1/2 overflow-visible"
      >
        <div className="relative flex items-center justify-center">
          <div className="absolute inset-0 rounded-full bg-amber-300/25 blur-2xl" />
          <span
            className="relative font-bold text-amber-400"
            style={{
              fontSize: config.iconSize + 14,
              lineHeight: 1.25,
              display: "inline-block",
              paddingTop: 2,
              paddingBottom: 2,
            }}
          >
            ੴ
          </span>
        </div>
      </motion.div>

      {/* ===============================
          TOP IK ONKAR
      =============================== */}
      <motion.div
        key={`top-ik-${cycle}`}
        initial={{
          opacity: 0,
          y: -12,
          scale: 0.7,
          rotateY: -180,
        }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1,
          rotateY: 0,
        }}
        transition={{
          duration: 0.9,
          delay: 1.35,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="relative z-20 mb-1 flex min-h-[32px] items-center justify-center overflow-visible pt-1"
      >
        <div className="relative">
          <motion.div
            animate={{
              opacity: [0.18, 0.34, 0.18],
              scale: [0.92, 1.12, 0.92],
            }}
            transition={{
              duration: 2.6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute inset-0 rounded-full bg-amber-300/18 blur-xl"
          />
          <span
            className="relative z-10 font-bold text-amber-400"
            style={{
              fontSize: config.iconSize,
              lineHeight: 1.35,
              display: "inline-block",
              paddingTop: 3,
              paddingBottom: 2,
            }}
          >
            ੴ
          </span>
        </div>
      </motion.div>

      {/* ===============================
          MAIN WORDMARK
      =============================== */}
      <div
        className="relative z-20 flex flex-col items-center justify-center px-2"
        style={{ maxWidth: config.maxWidth }}
      >
        {/* GOLDEN FLASH / RAYS */}
        <motion.div
          key={`flash-${cycle}`}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{
            opacity: [0, 0, 0.95, 0.35, 0],
            scale: [0.6, 0.6, 1.1, 1.45, 1.6],
          }}
          transition={{
            duration: 1.35,
            delay: 1.95,
            ease: "easeOut",
            times: [0, 0.45, 0.62, 0.8, 1],
          }}
          className="pointer-events-none absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(255,219,110,0.9) 0%, rgba(255,214,92,0.38) 28%, rgba(255,205,76,0.12) 52%, rgba(255,255,255,0) 72%)",
            filter: "blur(6px)",
          }}
        />

        {/* EXTRA WIDE RAYS */}
        <motion.div
          key={`wide-rays-${cycle}`}
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, 0, 0.38, 0.12, 0],
            scaleX: [0.4, 0.4, 1, 1.2, 1.25],
            scaleY: [0.4, 0.4, 1, 1.05, 1.1],
          }}
          transition={{
            duration: 1.55,
            delay: 1.9,
            ease: "easeOut",
          }}
          className="pointer-events-none absolute left-1/2 top-1/2 h-28 w-[140%] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(255,220,120,0.22) 0%, rgba(255,212,86,0.16) 25%, rgba(255,255,255,0) 70%)",
            filter: "blur(12px)",
          }}
        />

        {/* LETTERS */}
        <div className="relative flex items-center justify-center overflow-hidden px-2 py-1">
          {LETTERS.map((letter, index) => (
            <AnimatedLetter
              key={`${cycle}-${letter}-${index}`}
              letter={letter}
              index={index}
              fontSize={config.wordSize}
              gap={config.gap}
            />
          ))}

          {/* SLOW PREMIUM SHINE */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-md">
            <motion.div
              key={`shine-${cycle}`}
              initial={{ x: "-150%" }}
              animate={{ x: "190%" }}
              transition={{
                duration: 10,
                delay: 2.0,
                ease: "linear",
              }}
              className="absolute -top-[35%] h-[190%] w-24 skew-x-[-18deg]"
            >
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,250,235,0.03) 18%, rgba(255,238,170,0.78) 48%, rgba(255,248,220,0.14) 68%, rgba(255,255,255,0) 100%)",
                  filter: "blur(2px)",
                }}
              />
            </motion.div>
          </div>
        </div>

        {/* BOTTOM GOLD LINES */}
        <div className="mt-1.5 flex items-center justify-center gap-2">
          <motion.div
            key={`line-left-${cycle}`}
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{
              duration: 0.8,
              delay: 1.8,
              ease: "easeOut",
            }}
            className="origin-right h-px bg-gradient-to-l from-amber-300 via-amber-400 to-transparent"
            style={{ width: config.lineWidth }}
          />

          <motion.div
            key={`line-right-${cycle}`}
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{
              duration: 0.8,
              delay: 1.92,
              ease: "easeOut",
            }}
            className="origin-left h-px bg-gradient-to-r from-amber-300 via-amber-400 to-transparent"
            style={{ width: config.lineWidth }}
          />
        </div>

        {/* OPTIONAL TAGLINE */}
        {tagline ? (
          <motion.p
            key={`tagline-${cycle}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 2.1 }}
            className="mt-2 text-center text-[10px] font-semibold tracking-[0.14em] text-amber-200/70"
          >
            {tagline}
          </motion.p>
        ) : null}
      </div>
    </div>
  );

  if (onPress) {
    return (
      <button
        type="button"
        onClick={onPress}
        className="transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]"
      >
        {Content}
      </button>
    );
  }

  return Content;
}

function AnimatedLetter({ letter, index, fontSize, gap }) {
  return (
    <motion.span
      initial={{
        opacity: 0,
        y: 28,
        scale: 0.72,
        rotateX: -80,
      }}
      animate={{
        opacity: 1,
        y: [28, -5, 0],
        scale: [0.72, 1.08, 1],
        rotateX: [-80, 0, 0],
      }}
      transition={{
        duration: 0.72,
        delay: 1.05 + index * 0.11,
        times: [0, 0.68, 1],
        ease: "easeOut",
      }}
      className="relative inline-flex font-black text-amber-400"
      style={{
        fontSize,
        lineHeight: 1,
        marginLeft: gap,
        marginRight: gap,
        letterSpacing: "0.5px",
        textShadow:
          "0 0 10px rgba(251,191,36,0.10), 0 0 20px rgba(251,191,36,0.06)",
      }}
    >
      {letter}
    </motion.span>
  );
}

export default GurSetuWordmark;